import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/kit';
import { useMyDay, type NurseTask } from '../api';
import { dayRows, matchesQuery, rowTitle, searchCap, type DayItem } from '../dayModel';
import { shortName, useNow } from '../format';
import { NurseModal } from './NurseModal';

/* Each group shows a few; more than that is stated, never silent. */
const SHOWN = 2;

const KIND_NOUN: Record<string, string> = {
  dilate: 'dilation', vitals: 'vitals', injection: 'injection', observe: 'observation',
  consent: 'consent', chaperone: 'chaperone', dressing: 'dressing',
};

interface PatientHit {
  id: string;
  name: string;
  age: number | null;
  sex: 'F' | 'M' | null;
  tasks: NurseTask[];
  /** The row it opens — her first open task, else her first. */
  itemId: string;
}

function Hit({ strong, cap, onClick }: { strong: ReactNode; cap: ReactNode; onClick?: () => void }) {
  return (
    <div className="card" role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      style={onClick ? { cursor: 'pointer' } : undefined}>
      <div className="flex" style={{ gap: 10, alignItems: 'center' }}>
        <div className="grow" style={{ minWidth: 0 }}>
          <p className="t-strong">{strong}</p>
          <p className="t-cap mut">{cap}</p>
        </div>
        {onClick ? <Icon name="chevron-right" className="mut" /> : null}
      </div>
    </div>
  );
}

/**
 * THE SEARCH OVERLAY — `ison.srch`, the same on all eight nurse screens.
 *
 * Her own three, and nothing else: today's tasks, the patients on them, and
 * the shelf she can see. A task or a patient opens My day with that row
 * docked (router state — no PHI in the URL). The shelf is not kept in MAIE yet
 * (BACKEND-GAP), and its group says so rather than searching nothing.
 */
export function NurseSearch({
  open,
  onClose,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  /** On My day, dock the row in place; elsewhere, open My day with it docked. */
  onPick?: (itemId: string) => void;
}) {
  const navigate = useNavigate();
  const day = useMyDay();
  const [q, setQ] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const tasks = useMemo(() => day.data?.tasks ?? [], [day.data]);
  const now = useNow(open && tasks.some((t) => t.state === 'in_progress' && t.dueAt));

  useEffect(() => {
    if (!open) return;
    setQ('');
    const id = window.setTimeout(() => input.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  /* Open work first, then what is closed — the list she would act on. */
  const items = useMemo(() => {
    const all = dayRows(tasks, 'all', q);
    const live = (i: DayItem) => i.tasks.some((t) => (t.state === 'open' || t.state === 'in_progress') && !t.handedByMe);
    return [...all.filter(live), ...all.filter((i) => !live(i))];
  }, [tasks, q]);

  const patients = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const byId = new Map<string, PatientHit>();
    const rows = dayRows(tasks, 'all', '');
    /* A patient matches by name, or by any task of hers that matches —
       "dilat" finds the patient who is on a dilation. */
    const hitIds = new Set(
      rows.filter((i) => matchesQuery(i, needle)).flatMap((i) => i.tasks.map((t) => t.patient?.id)).filter(Boolean),
    );
    for (const t of tasks) {
      if (!t.patient) continue;
      if (needle && !hitIds.has(t.patient.id)) continue;
      const hit = byId.get(t.patient.id) ?? {
        id: t.patient.id, name: shortName(t.patient), age: t.patient.age ?? null, sex: t.patient.sex ?? null,
        tasks: [], itemId: '',
      };
      hit.tasks.push(t);
      byId.set(t.patient.id, hit);
    }
    for (const hit of byId.values()) {
      const openRow = rows.find((i) => i.tasks.some((t) => t.patient?.id === hit.id
        && (t.state === 'open' || t.state === 'in_progress') && !t.handedByMe));
      const anyRow = rows.find((i) => i.tasks.some((t) => t.patient?.id === hit.id));
      hit.itemId = (openRow ?? anyRow)?.id ?? '';
    }
    /* Patients with open work first. */
    const live = (p: PatientHit) => p.tasks.some((t) => (t.state === 'open' || t.state === 'in_progress') && !t.handedByMe);
    return [...byId.values()].sort((a, b) => Number(live(b)) - Number(live(a)));
  }, [tasks, q]);

  const pick = (itemId: string) => {
    onClose();
    if (onPick) onPick(itemId);
    else navigate('/nurse/day', { state: { select: itemId } });
  };

  const first = items[0]?.id ?? patients[0]?.itemId;
  const more = (shown: number, total: number) =>
    total > shown ? <p className="t-cap mut mt4">showing {shown} of {total} — type to narrow it</p> : null;

  return (
    <NurseModal
      open={open}
      onClose={onClose}
      title="Search"
      width={560}
      foot={<button type="button" className="btn btn-outline" onClick={onClose}>Close</button>}
    >
      <label className="srch mt12" style={{ marginLeft: 0, marginRight: 0, cursor: 'text' }}>
        <Icon name="search" />
        <input
          ref={input}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && first) pick(first);
          }}
          placeholder="a task, a patient, a shelf item…"
          aria-label="Search"
          className="grow"
          style={{ background: 'transparent', border: 'none', outline: 'none', color: 'inherit', font: 'inherit',
            width: 0, minWidth: 0 }}
        />
      </label>

      <p className="t-ovl mut" style={{ margin: '14px 0 6px' }}>Tasks — today</p>
      {!day.data ? (
        <p className="t-sec mut">Loading your day…</p>
      ) : items.length ? (
        <>
          {items.slice(0, SHOWN).map((i) => (
            <Hit key={i.id} strong={rowTitle(i)} cap={searchCap(i, now)} onClick={() => pick(i.id)} />
          ))}
          {more(Math.min(SHOWN, items.length), items.length)}
        </>
      ) : (
        <p className="t-sec mut">{q ? `No task today matches “${q}”.` : 'Nothing on your day yet.'}</p>
      )}

      <p className="t-ovl mut" style={{ margin: '14px 0 6px' }}>Patients — on today’s tasks</p>
      {!day.data ? null : patients.length ? (
        <>
          {patients.slice(0, SHOWN).map((p) => {
            const kinds = [...new Set(p.tasks.map((t) => KIND_NOUN[t.kind] ?? t.title.toLowerCase()))];
            const who = [p.name, [p.age, p.sex].filter((v) => v !== null && v !== undefined).join(' ')]
              .filter(Boolean).join(' · ');
            return (
              <Hit key={p.id} strong={who}
                cap={`${p.tasks.length} task${p.tasks.length === 1 ? '' : 's'} — ${kinds.join(', ')}`}
                onClick={p.itemId ? () => pick(p.itemId) : undefined} />
            );
          })}
          {more(Math.min(SHOWN, patients.length), patients.length)}
        </>
      ) : (
        <p className="t-sec mut">{q ? `No patient on today’s tasks matches “${q}”.` : 'No patient on today’s tasks yet.'}</p>
      )}

      <p className="t-ovl mut" style={{ margin: '14px 0 6px' }}>The shelf</p>
      <Hit strong="The shelf list is not kept in MAIE yet"
        cap="nothing on it to search — Stock is where you flag what is low"
        onClick={() => {
          onClose();
          navigate('/nurse/stock');
        }} />

      <p className="t-cap mut center mt12">
        her own three — today’s tasks, the patients on them, and the shelf she can see. Nothing else is hers to search.
      </p>
    </NurseModal>
  );
}
